import { useCallback, useMemo, useState } from 'react';
import { type Row } from '@tanstack/react-table';
import { ExportScopeType } from '@/app/enums/ExportScopeType';
import { type ExportRequestDto } from '@/app/models/ExportRequestDto';
import { type DataTableColumnDef } from '../../../models/DataTableColumnDef';
import { exportToCSV } from '../../../utils/exportToCSV';
import { exportToExcelFromConfig } from '../../../utils/exportToExcel';
import { exportToPDFFromConfig } from '../../../utils/exportToPDF';
import { buildFilename, normalizeExportLabel } from '../core/_utils';
import {
  type BackendExport,
  type ExportColumnConfig,
  type ExportFieldDef,
  type ExportFormat,
  type ExportOrientation,
} from '../types';

interface UseExportConfigParams {
  columns: DataTableColumnDef<any>[];
  exportFields?: ExportFieldDef[];
  exportColumnKeys?: string[];
  exportColumnLabels?: Record<string, string>;
  exportableFormats: string[];
  isBackend: boolean;
  defaultScope?: ExportScopeType;
  pageTitle?: string;
  pageRange?: string;
}

interface UseExportConfigReturn {
  // Available fields
  availableFields: ExportFieldDef[];

  // State
  selectedKeys: string[];
  headerOverrides: Partial<Record<string, string>>;
  format: ExportFormat;
  rowScope: ExportScopeType;
  orientation: ExportOrientation;

  // Actions
  toggleColumn: (key: string) => void;
  selectAllColumns: () => void;
  deselectAllColumns: () => void;
  reorderColumns: (fromIndex: number, toIndex: number) => void;
  /** Uma posição para cima (-1) ou para baixo (1): é o caminho de reordenar sem mouse. */
  moveColumn: (key: string, direction: -1 | 1) => void;
  setHeaderOverride: (key: string, label: string) => void;
  /** Nome da coluna no arquivo: o escolhido ou, em branco, o rótulo do catálogo. */
  headerOf: (key: string) => string;
  setFormat: (format: ExportFormat) => void;
  setRowScope: (scope: ExportScopeType) => void;
  setOrientation: (orientation: ExportOrientation) => void;
  execute: (currentPageRows: Record<string, any>[], selectedRows: Row<object>[]) => void;
  toExportRequest: (backend: BackendExport) => ExportRequestDto;
}

function deriveFieldsFromColumns(columns: DataTableColumnDef<any>[]): ExportFieldDef[] {
  return columns
    .filter((col) => col.isPrintable !== false && col.accessorKey)
    .map((col) => ({
      key: col.accessorKey!,
      label: String(col.header || col.accessorKey || ''),
      format: col.print
        ? (value: any) => {
            return value !== undefined && value !== null ? String(value) : '';
          }
        : undefined,
    }));
}

export function useExportConfig({
  columns,
  exportFields,
  exportColumnKeys,
  exportColumnLabels,
  exportableFormats,
  isBackend,
  defaultScope = ExportScopeType.PAGE,
  pageTitle,
  pageRange,
}: UseExportConfigParams): UseExportConfigReturn {
  const availableFields = useMemo(() => {
    return exportFields ?? deriveFieldsFromColumns(columns);
  }, [columns, exportFields]);

  const defaultSelectedKeys = useMemo(() => {
    if (exportFields) {
      if (exportColumnKeys) {
        const available = new Set(availableFields.map((field) => field.key));
        return exportColumnKeys.filter((key) => available.has(key));
      }
      const printable = columns.filter((col) => col.isPrintable !== false);
      const tableKeys = new Set(
        printable.flatMap((col) => (isBackend ? [col.accessorKey, col.id] : [col.accessorKey])).filter(Boolean),
      );
      const tableHeaders = new Set(
        printable
          .filter((col) => typeof col.header === 'string')
          .map((col) => normalizeExportLabel(String(col.header))),
      );
      return availableFields
        .filter(
          (field) => tableKeys.has(field.key) || (isBackend && tableHeaders.has(normalizeExportLabel(field.label))),
        )
        .map((field) => field.key);
    }
    return availableFields.map((field) => field.key);
  }, [availableFields, columns, exportFields, exportColumnKeys, isBackend]);

  const defaultFormat = useMemo(() => {
    if (exportableFormats.includes('pdf')) return 'pdf' as ExportFormat;
    if (exportableFormats.includes('csv')) return 'csv' as ExportFormat;
    if (exportableFormats.includes('xlsx')) return 'xlsx' as ExportFormat;
    return 'pdf' as ExportFormat;
  }, [exportableFormats]);

  const [selectedKeys, setSelectedKeys] = useState<string[]>(defaultSelectedKeys);
  const [headerOverrides, setHeaderOverrides] = useState<Partial<Record<string, string>>>({});
  const [format, setFormat] = useState<ExportFormat>(defaultFormat);
  const [rowScope, setRowScope] = useState(defaultScope);
  const [orientation, setOrientation] = useState<ExportOrientation>('landscape');

  const toggleColumn = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  }, []);

  // Inclui as que faltam no fim: a ordem já escolhida é trabalho do usuário.
  const selectAllColumns = useCallback(() => {
    setSelectedKeys((prev) => [...prev, ...availableFields.map((f) => f.key).filter((key) => !prev.includes(key))]);
  }, [availableFields]);

  const deselectAllColumns = useCallback(() => {
    setSelectedKeys([]);
  }, []);

  const reorderColumns = useCallback((fromIndex: number, toIndex: number) => {
    setSelectedKeys((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  }, []);

  const moveColumn = useCallback((key: string, direction: -1 | 1) => {
    setSelectedKeys((prev) => {
      const from = prev.indexOf(key);
      const to = from + direction;
      if (from === -1 || to < 0 || to >= prev.length) return prev;
      const updated = [...prev];
      [updated[from], updated[to]] = [updated[to], updated[from]];
      return updated;
    });
  }, []);

  const setHeaderOverride = useCallback((key: string, label: string) => {
    setHeaderOverrides((prev) => {
      if (label === '') {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: label };
    });
  }, []);

  const execute = useCallback(
    (currentPageRows: Record<string, any>[], selectedRows: Row<object>[]) => {
      const rows =
        rowScope === ExportScopeType.SELECTED
          ? selectedRows.map((r) => r.original as Record<string, any>)
          : currentPageRows;

      const fieldMap = new Map(availableFields.map((f) => [f.key, f]));

      const exportColumns: ExportColumnConfig[] = selectedKeys
        .map((key) => {
          const field = fieldMap.get(key);
          if (!field) return null;
          return {
            key: field.key,
            header: headerOverrides[key]?.trim() || field.label,
            format: field.format,
          };
        })
        .filter(Boolean) as ExportColumnConfig[];

      const config = {
        columns: exportColumns,
        rows,
        orientation,
        filename: buildFilename(pageTitle, pageRange, format),
      };

      switch (format) {
        case 'pdf':
          exportToPDFFromConfig(config);
          break;
        case 'xlsx':
          exportToExcelFromConfig(config);
          break;
        case 'csv':
          exportToCSV(config);
          break;
      }
    },
    [selectedKeys, headerOverrides, format, rowScope, orientation, availableFields, pageTitle, pageRange],
  );

  // Rótulo em branco o backend recusa: nome só com espaços volta ao do catálogo.
  const headerOf = (key: string) =>
    headerOverrides[key]?.trim() ||
    exportColumnLabels?.[key] ||
    availableFields.find((field) => field.key === key)?.label ||
    key;

  const toExportRequest = (backend: BackendExport): ExportRequestDto => {
    // Só "Todas as páginas" deixa o backend repetir a consulta; os outros dois escopos já sabem quais linhas
    // querem, e mandá-las por id dispensa o backend de entender de paginação.
    const ids: Record<ExportScopeType, string[] | undefined> = {
      [ExportScopeType.ALL]: undefined,
      [ExportScopeType.PAGE]: backend.pageIds,
      [ExportScopeType.SELECTED]: backend.selectedIds,
    };
    const chosen = ids[rowScope];
    return {
      query: backend.query,
      ...(chosen ? { ids: chosen } : {}),
      format: format === 'xlsx' ? 'XLSX' : 'PDF',
      orientation: orientation === 'portrait' ? 'PORTRAIT' : 'LANDSCAPE',
      columns: selectedKeys.map((key) => ({ key, label: headerOf(key) })),
      filters: backend.filters,
    };
  };

  return {
    availableFields,
    selectedKeys,
    headerOverrides,
    format,
    rowScope,
    orientation,
    toggleColumn,
    selectAllColumns,
    deselectAllColumns,
    reorderColumns,
    moveColumn,
    setHeaderOverride,
    headerOf,
    setFormat,
    setRowScope,
    setOrientation,
    execute,
    toExportRequest,
  };
}
